import React, { useState } from "react";
import { toast } from "sonner";
import { X, CheckCircle2, Copy } from "lucide-react";
import { submitReservation } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { T } from "@/components/T";

const NON_BINDING_NOTICE = "This is a non-binding reservation, not a completed purchase. Price, availability, shipping charges and delivery schedule will be confirmed before payment.";

const empty = (defaults) => ({
  full_name: "", email: "", phone: "",
  city: "", state: "", country: "",
  edition_language: defaults?.edition_language || "",
  quantity: 1,
  notes: "",
  consent_privacy: false,
  website: "", // honeypot
});

export const ReserveModal = ({ book, edition, onClose }) => {
  const { t } = useLang();
  const [form, setForm] = useState(empty({ edition_language: edition?.language || book?.language || "English" }));
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_privacy) return toast.error(t("err.consent_required", "Please tick the consent box."));
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim() || !form.city.trim() || !form.country.trim()) {
      return toast.error(t("err.required_all", "Please fill all required fields."));
    }
    setSubmitting(true);
    try {
      const r = await submitReservation({
        book_id: book.id,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        country: form.country.trim(),
        edition_language: form.edition_language,
        quantity: Number(form.quantity) || 1,
        notes: form.notes.trim(),
        consent_privacy: true,
        website: form.website, // honeypot (blank for humans)
      });
      setConfirmation(r);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.reference_number) {
        toast.error(`${detail.message} Existing reference: ${detail.reference_number}`);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || "Please check the form.");
      } else {
        toast.error(t("err.retry_later", "Reservation could not be submitted — please try again shortly."));
      }
    } finally { setSubmitting(false); }
  };

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(confirmation.reference_number);
      toast.success("Reference number copied");
    } catch { /* no-op */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(24,20,18,0.7)" }} onClick={onClose} data-testid="reservation-modal">
      <div className="relative bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto border" style={{ borderColor: "var(--gi-border)" }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 hover:bg-[var(--gi-ivory-2)]" aria-label="Close" data-testid="reservation-close-btn">
          <X size={18} />
        </button>

        {confirmation ? (
          <div className="p-8" data-testid="reservation-confirmation">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={28} style={{ color: "var(--gi-green)" }} />
              <div className="font-heading text-2xl" style={{ color: "var(--gi-maroon)" }}><T textKey="res.confirmation.title" fallback="Reservation received" /></div>
            </div>
            <p className="mt-4 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
              <T textKey="res.confirmation.thanks" fallback="Thank you. Your reservation has been recorded. Please keep this reference number for your records; the publisher will contact you before payment." />
            </p>
            <div className="mt-6 border p-5 flex items-center justify-between gap-4" style={{ borderColor: "var(--gi-border)", background: "var(--gi-ivory-2)" }}>
              <div>
                <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}><T textKey="res.reference_number" fallback="Reference number" /></div>
                <div className="mt-1 font-heading text-xl" style={{ color: "var(--gi-maroon)" }} data-testid="reservation-reference">{confirmation.reference_number}</div>
              </div>
              <button onClick={copyRef} className="btn-ghost" data-testid="reservation-copy-btn"><Copy size={12} /> <T textKey="common.copy" fallback="Copy" /></button>
            </div>
            <div className="mt-6 p-4 border-l-2 text-xs" style={{ borderColor: "var(--gi-gold)", background: "var(--gi-ivory-2)", color: "var(--gi-charcoal-2)" }}>
              <T textKey="res.notice" fallback={NON_BINDING_NOTICE} />
            </div>
            <div className="mt-6">
              <button onClick={onClose} className="btn-maroon" data-testid="reservation-done-btn"><T textKey="common.close" fallback="Close" /></button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 space-y-5" data-testid="reservation-form">
            <div>
              <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}><T textKey="res.header" fallback="Reserve print copy" /></div>
              <div className="mt-2 font-heading text-2xl leading-tight" style={{ color: "var(--gi-maroon)" }}>{book.title}</div>
              {book.reservation_expected_release_note && (
                <p className="mt-2 text-xs" style={{ color: "var(--gi-muted)" }} data-testid="reservation-release-note">{book.reservation_expected_release_note}</p>
              )}
            </div>

            <Row>
              <Field labelKey="res.name" labelFallback="Full name" required value={form.full_name} onChange={v => set("full_name", v)} testId="reservation-name" autoComplete="name" />
              <Field labelKey="res.email" labelFallback="Email" required type="email" value={form.email} onChange={v => set("email", v)} testId="reservation-email" autoComplete="email" />
            </Row>
            <Row>
              <Field labelKey="res.phone" labelFallback="Phone / WhatsApp" required value={form.phone} onChange={v => set("phone", v)} testId="reservation-phone" autoComplete="tel" />
              <Field labelKey="res.edition" labelFallback="Preferred edition / language" value={form.edition_language} onChange={v => set("edition_language", v)} testId="reservation-edition" />
            </Row>
            <Row>
              <Field labelKey="res.city" labelFallback="City" required value={form.city} onChange={v => set("city", v)} testId="reservation-city" autoComplete="address-level2" />
              <Field labelKey="res.state" labelFallback="State / province" value={form.state} onChange={v => set("state", v)} testId="reservation-state" autoComplete="address-level1" />
            </Row>
            <Row>
              <Field labelKey="res.country" labelFallback="Country" required value={form.country} onChange={v => set("country", v)} testId="reservation-country" autoComplete="country-name" />
              <div>
                <label className="gi-label"><T textKey="res.quantity" fallback="Number of copies" /></label>
                <input type="number" min={1} max={20} className="gi-input" value={form.quantity} onChange={e => set("quantity", e.target.value)} data-testid="reservation-quantity" />
              </div>
            </Row>
            <div>
              <label className="gi-label"><T textKey="res.notes" fallback="Optional note" /></label>
              <textarea rows={2} className="gi-input" value={form.notes} onChange={e => set("notes", e.target.value)} data-testid="reservation-notes" />
            </div>

            {/* Honeypot — leave empty; hidden from users */}
            <div style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }} aria-hidden="true">
              <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={e => set("website", e.target.value)} /></label>
            </div>

            <div className="p-4 border-l-2 text-xs" style={{ borderColor: "var(--gi-gold)", background: "var(--gi-ivory-2)", color: "var(--gi-charcoal-2)" }} data-testid="reservation-notice">
              <T textKey="res.notice" fallback={NON_BINDING_NOTICE} />
            </div>

            <label className="flex items-start gap-3 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
              <input type="checkbox" checked={form.consent_privacy} onChange={e => set("consent_privacy", e.target.checked)} className="mt-1" data-testid="reservation-consent" />
              <span><T textKey="res.consent" fallback="I consent to the publisher storing this information solely to process my reservation and contact me about this title." /></span>
            </label>

            <div className="flex gap-3 justify-end">
              <button type="button" onClick={onClose} className="btn-ghost" data-testid="reservation-cancel-btn"><T textKey="common.cancel" fallback="Cancel" /></button>
              <button type="submit" disabled={submitting} className="btn-maroon" data-testid="reservation-submit-btn">
                {submitting ? "Submitting…" : <T textKey="res.submit" fallback="Submit reservation" />}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Row = ({ children }) => <div className="grid gap-5 sm:grid-cols-2">{children}</div>;
const Field = ({ label, labelKey, labelFallback, value, onChange, required, type = "text", testId, autoComplete }) => (
  <div>
    <label className="gi-label">
      {labelKey ? <T textKey={labelKey} fallback={labelFallback} /> : label}
      {required && " *"}
    </label>
    <input type={type} autoComplete={autoComplete} className="gi-input" value={value} onChange={e => onChange(e.target.value)} data-testid={testId} />
  </div>
);

export default ReserveModal;
